import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AddressApi, CouponApi, MetaApi, OrderApi, PaymentApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLocation } from '../context/LocationContext';
import { PageHero } from '../components/Common';
import AddressForm from '../components/AddressForm';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

export default function CartPage() {
  const navigate = useNavigate();
  const { isLoggedIn, user } = useAuth();
  const { items, subtotal, grossMrp, setQty, remove, clear, repriceForPincode } = useCart();
  const { location, setPincode } = useLocation();

  const [addresses, setAddresses] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // address being edited, if any

  const [delivery, setDelivery] = useState({ free: true, fee: 0, freeAbove: 0 });
  const [razorpay, setRazorpay] = useState({ enabled: false, keyId: '' });

  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponErr, setCouponErr] = useState('');
  const [availableCoupons, setAvailableCoupons] = useState([]);

  const [placing, setPlacing] = useState(false);
  const [err, setErr] = useState('');

  const loadAddresses = () =>
    AddressApi.list()
      .then((d) => {
        const list = d.items || [];
        setAddresses(list);
        setSelected((cur) => cur || list.find((a) => a.isDefault) || list[0] || null);
      })
      .catch(() => {});

  useEffect(() => { if (isLoggedIn) loadAddresses(); }, [isLoggedIn]);

  // Delivery PIN code drives coupon targeting: the chosen address, else the
  // browsing location.
  const activePincode = selected?.pincode || location?.pincode || '';

  // Coupons the admin has published — shown in the cart so matching ones can be
  // applied in one tap, exactly like the app. Filtered to the PIN code.
  useEffect(() => {
    if (!isLoggedIn) { setAvailableCoupons([]); return; }
    CouponApi.public(activePincode).then((d) => setAvailableCoupons(d.items || [])).catch(() => setAvailableCoupons([]));
  }, [isLoggedIn, activePincode]);

  // The chosen address IS the delivery location: adopt its pincode and re-price
  // every line for that state/district, exactly like the app.
  useEffect(() => {
    const pin = selected?.pincode;
    if (!pin) return;
    if (pin !== location?.pincode) setPincode(pin).catch(() => {});
    repriceForPincode(pin, {
      state: selected?.state || '',
      district: selected?.district || selected?.city || '',
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?._id, selected?.pincode]);

  useEffect(() => {
    MetaApi.config()
      .then((d) => {
        if (d.delivery) setDelivery(d.delivery);
        if (d.razorpay) setRazorpay(d.razorpay);
      })
      .catch(() => {});
  }, []);

  // ---- totals
  const productDiscount = Math.max(grossMrp - subtotal, 0);
  const productOff = grossMrp > 0 ? Math.round((productDiscount / grossMrp) * 100) : 0;
  const discount = coupon?.discount || 0;
  const afterDiscount = subtotal - discount;

  // What delivery would have cost — shown struck through when it's waived, so
  // the shopper can see the benefit they're getting.
  const deliveryReference = delivery.fee || 0;
  let deliveryFee = 0;
  if (!delivery.free) {
    const qualifies = delivery.freeAbove > 0 && afterDiscount >= delivery.freeAbove;
    deliveryFee = qualifies ? 0 : deliveryReference;
  }
  const deliverySaved = deliveryFee === 0 ? deliveryReference : 0;

  const total = Math.max(afterDiscount + deliveryFee, 0);
  const totalSaved = productDiscount + discount + deliverySaved;

  // How much more to spend to unlock free delivery.
  const awayFromFree =
    !delivery.free && deliveryFee > 0 && delivery.freeAbove > 0
      ? delivery.freeAbove - afterDiscount
      : 0;

  const applyCoupon = async (codeArg) => {
    setCouponErr('');
    const code = String(codeArg || couponInput).trim().toUpperCase();
    if (!code) return;
    if (!isLoggedIn) { navigate('/login'); return; }
    try {
      const lineItems = items.map((i) => ({ productId: i.productId, price: i.unit, qty: i.qty }));
      const res = await CouponApi.validate(code, subtotal, lineItems, activePincode);
      if (res.valid) { setCoupon(res); setCouponInput(''); }
      else { setCoupon(null); setCouponErr(res.message || 'Invalid coupon code'); }
    } catch (e) { setCouponErr(e.message); }
  };

  // Offer summary line, e.g. "15% OFF up to ₹200" or "₹100 OFF".
  const offerText = (c) =>
    c.type === 'percent'
      ? `${c.value}% OFF${c.maxDiscount ? ` up to ₹${c.maxDiscount}` : ''}`
      : `₹${c.value} OFF`;

  const placeOrder = async () => {
    setErr('');
    if (!isLoggedIn) { navigate('/login'); return; }
    if (!selected) { setErr('Please select a delivery address'); return; }

    setPlacing(true);
    try {
      const payload = {
        items: items.map((i) => ({ productId: i.productId, weight: i.weight, qty: i.qty })),
        address: {
          fullName: selected.fullName,
          phone: selected.phone,
          line1: selected.line1,
          line2: selected.line2,
          city: selected.city,
          state: selected.state,
          pincode: selected.pincode,
          type: selected.type,
        },
        paymentMethod: 'online',
        couponCode: coupon?.code || '',
      };

      if (razorpay.enabled) {
        const rp = await PaymentApi.razorpayOrder(total);
        const checkout = await openRazorpay({
          key: rp.keyId || razorpay.keyId,
          order_id: rp.orderId,
          amount: rp.amount,
          currency: rp.currency || 'INR',
          name: 'Golden Taj',
          prefill: { name: selected.fullName, contact: selected.phone, email: user?.email || '' },
          theme: { color: '#7c2528' },
        });
        payload.razorpayOrderId = rp.orderId;
        payload.razorpayPaymentId = checkout.razorpay_payment_id;
        payload.razorpaySignature = checkout.razorpay_signature;
      }

      const { order } = await OrderApi.create(payload);
      clear();
      navigate(`/order-success?id=${order._id}&no=${order.orderNumber}`, { replace: true });
    } catch (e) {
      setErr(e.message || 'Could not place your order.');
    } finally {
      setPlacing(false);
    }
  };

  if (!items.length) {
    return (
      <>
        <PageHero title="Your Cart" />
        <section>
          <div className="container empty-state">
            <i className="fas fa-cart-shopping" />
            <h3>Your cart is empty</h3>
            <p className="muted">Browse our premium rice collection and add your favourites.</p>
            <Link className="btn-gold" to="/">Start Shopping</Link>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHero title="Your Cart" />
      <section>
        <div className="container cart-layout">
          <div>
            {/* Delivery address — chosen before reviewing the items, since it
                drives the pricing and the delivery fee. */}
            <div className="panel-card">
              <div className="row-between">
                <h3><i className="fas fa-location-dot" /> Delivery Address</h3>
                {isLoggedIn ? (
                  <button className="link-btn inline" onClick={() => { setEditing(null); setShowForm(true); }}>
                    + Add new
                  </button>
                ) : null}
              </div>

              {!isLoggedIn ? (
                <p className="muted">
                  <Link className="link-btn inline" to="/login">Login</Link> to select a delivery address.
                </p>
              ) : addresses.length === 0 ? (
                <p className="muted">No saved addresses yet — add one to continue.</p>
              ) : (
                <div className="addr-list">
                  {addresses.map((a) => (
                    <div key={a._id} className={`addr-option ${selected?._id === a._id ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="addr"
                        checked={selected?._id === a._id}
                        onChange={() => setSelected(a)}
                        id={`addr-${a._id}`}
                      />
                      <label htmlFor={`addr-${a._id}`} style={{ flex: 1, cursor: 'pointer', padding: 0, border: 0 }}>
                        <strong>
                          {a.fullName} <span className="addr-type">{a.type}</span>
                          {a.isDefault ? <span className="addr-type default">Default</span> : null}
                        </strong>
                        <p className="muted small">
                          {[a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(', ')}
                        </p>
                        <p className="muted small">+91 {a.phone}</p>
                      </label>
                      <button
                        className="link-btn inline"
                        onClick={() => { setEditing(a); setShowForm(true); }}
                      >
                        Edit
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lines */}
            <h3 className="cart-section-title">
              <i className="fas fa-cart-shopping" /> Your Items ({items.length})
            </h3>
            {items.map((it) => {
              const lineOff = it.mrp > it.unit ? Math.round(((it.mrp - it.unit) / it.mrp) * 100) : 0;
              return (
                <div className="cart-line" key={`${it.productId}-${it.weight}`}>
                  <Link to={`/product/${it.slug}`}>
                    {it.image ? <img src={it.image} alt={it.title} /> : <div className="product-noimg" style={{ height: 90 }} />}
                  </Link>

                  <div className="cart-line-body">
                    <Link to={`/product/${it.slug}`}><h4>{it.title}</h4></Link>
                    <span className="muted small">{it.brand} · {it.weight}</span>

                    {it.bulkApplied ? (
                      <span className="bulk-tag">🎉 Bulk price applied ({it.moq}+ units)</span>
                    ) : it.moq > 0 && it.moqPrice > 0 ? (
                      <span className="muted small">Buy {it.moq}+ at {inr(it.moqPrice)}/unit</span>
                    ) : null}

                    <div className="cart-line-foot">
                      <div className="qty">
                        <button onClick={() => setQty(it.productId, it.weight, it.qty - 1)}>−</button>
                        <span>{it.qty}</span>
                        <button onClick={() => setQty(it.productId, it.weight, it.qty + 1)}>+</button>
                      </div>
                      <div className="price-row" style={{ margin: 0, justifyContent: 'flex-end' }}>
                        <span className="price-now">{inr(it.unit * it.qty)}</span>
                        {it.mrp > it.unit ? <span className="price-mrp">{inr(it.mrp * it.qty)}</span> : null}
                        {lineOff > 0 ? <span className="price-off">{lineOff}% OFF</span> : null}
                      </div>
                    </div>
                  </div>

                  <button className="line-remove" onClick={() => remove(it.productId, it.weight)} aria-label="Remove">
                    <i className="fas fa-xmark" />
                  </button>
                </div>
              );
            })}

            {/* Coupon */}
            <div className="coupon-card">
              {coupon ? (
                <div className="coupon-applied">
                  <div className="coupon-badge ok"><i className="fas fa-check" /></div>
                  <div className="coupon-info">
                    <strong>{coupon.code} applied</strong>
                    <span>You saved {inr(coupon.discount)} on this order</span>
                  </div>
                  <button className="coupon-remove" onClick={() => { setCoupon(null); setCouponErr(''); }}>
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <div className="coupon-head">
                    <div className="coupon-badge"><i className="fas fa-tag" /></div>
                    <div className="coupon-info">
                      <strong>Have a coupon?</strong>
                      <span>Apply a code to save more on this order</span>
                    </div>
                  </div>
                  <div className="coupon-row">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="ENTER CODE"
                      onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                    />
                    <button className="coupon-apply" disabled={!couponInput.trim()} onClick={() => applyCoupon()}>
                      Apply
                    </button>
                  </div>

                  {/* Available coupons — eligible ones get a glowing Apply, the
                      rest show why they can't be used, mirroring the app. */}
                  {availableCoupons.length ? (
                    <div className="coupon-offers">
                      <span className="coupon-offers-label">Available coupons</span>
                      {availableCoupons.map((c) => {
                        const eligible = !c.used && subtotal >= (c.minOrder || 0);
                        const reason = c.used
                          ? 'Already used'
                          : `Add ${inr((c.minOrder || 0) - subtotal)} more to use`;
                        return (
                          <div key={c.code} className={`coupon-offer${eligible ? '' : ' disabled'}`}>
                            <div className="coupon-offer-info">
                              <div className="coupon-offer-code">{c.code}</div>
                              <strong>{offerText(c)}</strong>
                              {c.description ? <span>{c.description}</span> : null}
                              {c.terms ? <span className="coupon-offer-terms">{c.terms}</span> : null}
                              {!eligible ? <span className="coupon-offer-reason">{reason}</span> : null}
                            </div>
                            {eligible ? (
                              <button className="coupon-offer-apply" onClick={() => applyCoupon(c.code)}>
                                Apply
                              </button>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </>
              )}
              {couponErr ? <p className="form-msg err">{couponErr}</p> : null}
            </div>

            {/* Payment */}
            <div className="panel-card">
              <h3>Payment Method</h3>
              <label className="addr-option active">
                <input type="radio" checked readOnly />
                <div>
                  <strong>Pay Online</strong>
                  <p className="muted small">UPI / Card / Netbanking — secured by Razorpay</p>
                </div>
              </label>
            </div>
          </div>

          {/* Bill */}
          <aside className="bill-card">
            <h3>Bill Details</h3>

            {productDiscount > 0 ? (
              <>
                <div className="bill-row"><span>Total MRP</span><span>{inr(grossMrp)}</span></div>
                <div className="bill-row">
                  <span>Product discount ({productOff}% off)</span>
                  <span className="ok">− {inr(productDiscount)}</span>
                </div>
              </>
            ) : null}

            <div className="bill-row"><span>Item total</span><span>{inr(subtotal)}</span></div>

            {discount > 0 ? (
              <div className="bill-row"><span>Coupon ({coupon.code})</span><span className="ok">− {inr(discount)}</span></div>
            ) : null}

            <div className="bill-row">
              <span>Delivery fee</span>
              <span className="delivery-val">
                {deliveryFee === 0 ? (
                  <>
                    {deliveryReference > 0 ? (
                      <span className="price-mrp">{inr(deliveryReference)}</span>
                    ) : null}
                    <span className="ok">FREE</span>
                  </>
                ) : (
                  inr(deliveryFee)
                )}
              </span>
            </div>

            {awayFromFree > 0 ? (
              <p className="free-hint">
                <i className="fas fa-truck-fast" /> Add {inr(awayFromFree)} more to get free delivery
              </p>
            ) : null}

            <div className="bill-total"><span>To Pay</span><span>{inr(total)}</span></div>

            {totalSaved > 0 ? (
              <div className="saved-banner">
                🎉 You save {inr(totalSaved)} on this order
                {deliverySaved > 0 ? (
                  <span className="saved-sub">incl. {inr(deliverySaved)} delivery</span>
                ) : null}
              </div>
            ) : null}

            {location?.pincode ? (
              <p className="muted small" style={{ marginTop: 10 }}>
                <i className="fas fa-location-dot" /> Delivering to {location.pincode}
              </p>
            ) : null}

            {err ? <p className="form-msg err">{err}</p> : null}

            <button
              className="consult-btn"
              style={{ width: '100%', justifyContent: 'center', marginTop: 14 }}
              disabled={placing}
              onClick={placeOrder}
            >
              {placing ? 'Placing order…' : `Pay ${inr(total)}`}
            </button>

            <Link className="link-btn" to="/">Continue shopping</Link>
          </aside>
        </div>
      </section>

      {showForm ? (
        <AddressForm
          address={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={(saved) => {
            setShowForm(false);
            setEditing(null);
            // Keep the edited address selected so pricing follows it.
            if (saved?._id && selected?._id === saved._id) setSelected(saved);
            loadAddresses();
          }}
        />
      ) : null}
    </>
  );
}

// Loads Razorpay's checkout script once, then opens it and resolves on success.
function openRazorpay(options) {
  return new Promise((resolve, reject) => {
    const start = () => {
      if (!window.Razorpay) { reject(new Error('Payment could not be started.')); return; }
      const rz = new window.Razorpay({
        ...options,
        handler: (resp) => resolve(resp),
        modal: { ondismiss: () => reject(new Error('Payment cancelled')) },
      });
      rz.open();
    };

    if (window.Razorpay) { start(); return; }
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = start;
    s.onerror = () => reject(new Error('Could not load the payment gateway.'));
    document.body.appendChild(s);
  });
}
